import { Header } from "@/components/Header";
import { PremiumFooter } from "@/components/landing/PremiumFooter";
import { Newspaper, Download, Mail, ExternalLink, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";

const pressReleases = [
  {
    date: "3 décembre 2024",
    title: "Floop lance son système de crédits révolutionnaire",
    excerpt: "Une nouvelle façon de payer uniquement ce que vous utilisez, avec une transparence totale.",
  },
  {
    date: "15 novembre 2024",
    title: "Lancement officiel de Floop : l'IA générative accessible à tous",
    excerpt: "Après plusieurs mois de beta, Floop ouvre ses portes au grand public avec une offre complète.",
  },
  {
    date: "1 octobre 2024",
    title: "Floop Pro : la qualité studio pour tous les créateurs",
    excerpt: "Notre nouveau modèle IA offre des résultats professionnels en quelques secondes.",
  },
];

const mediaFeatures = [
  { name: "TechCrunch", logo: "TC", quote: "Floop redéfinit la création visuelle assistée par IA" },
  { name: "Les Échos", logo: "LE", quote: "La startup française qui démocratise l'art numérique" },
  { name: "Maddyness", logo: "M", quote: "Un outil indispensable pour les créateurs de contenu" },
  { name: "FrenchWeb", logo: "FW", quote: "L'IA générative made in France qui fait parler d'elle" },
];

const Press = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="pt-24 pb-16 text-center">
        <div className="container mx-auto px-6">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <Newspaper className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 font-display">
            Espace Presse
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Retrouvez nos communiqués de presse, ressources médias et informations 
            pour les journalistes.
          </p>
          <Button className="bg-gradient-floop hover:opacity-90 text-white rounded-full px-6">
            <Mail className="w-4 h-4 mr-2" />
            Contacter l'équipe presse
          </Button>
        </div>
      </section>

      {/* Media Kit */}
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-card border border-border rounded-3xl p-8">
              <h2 className="text-2xl font-bold text-foreground mb-6">Kit Média</h2>
              <p className="text-muted-foreground mb-6">
                Téléchargez notre kit média contenant logos, visuels et informations sur Floop.
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <button className="p-4 bg-secondary rounded-xl text-left hover:bg-secondary/80 transition-colors">
                  <Download className="w-5 h-5 text-primary mb-2" />
                  <div className="font-medium text-foreground">Logos</div>
                  <div className="text-sm text-muted-foreground">PNG, SVG, AI</div>
                </button>
                <button className="p-4 bg-secondary rounded-xl text-left hover:bg-secondary/80 transition-colors">
                  <Download className="w-5 h-5 text-primary mb-2" />
                  <div className="font-medium text-foreground">Screenshots</div>
                  <div className="text-sm text-muted-foreground">Haute résolution</div>
                </button>
                <button className="p-4 bg-secondary rounded-xl text-left hover:bg-secondary/80 transition-colors">
                  <Download className="w-5 h-5 text-primary mb-2" />
                  <div className="font-medium text-foreground">Fact Sheet</div>
                  <div className="text-sm text-muted-foreground">PDF</div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Press Releases */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-foreground mb-8 text-center font-display">
            Communiqués de presse
          </h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {pressReleases.map((release, index) => (
              <div
                key={index}
                className="bg-card border border-border rounded-2xl p-6 hover:border-primary/50 transition-colors cursor-pointer group"
              >
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                  <Calendar className="w-4 h-4" />
                  {release.date}
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                  {release.title}
                </h3>
                <p className="text-muted-foreground">{release.excerpt}</p>
                <button className="flex items-center gap-1 text-primary text-sm font-medium mt-4">
                  Lire le communiqué
                  <ExternalLink className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Media Coverage */}
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-foreground mb-8 text-center font-display">
            Ils parlent de nous
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {mediaFeatures.map((media, index) => (
              <div
                key={index}
                className="bg-card border border-border rounded-2xl p-6 text-center"
              >
                <div className="w-12 h-12 rounded-full bg-gradient-floop flex items-center justify-center mx-auto mb-4 text-white font-bold">
                  {media.logo}
                </div>
                <div className="font-semibold text-foreground mb-2">{media.name}</div>
                <p className="text-sm text-muted-foreground italic">"{media.quote}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4">
            Contact Presse
          </h2>
          <p className="text-muted-foreground mb-6">
            Pour toute demande presse, interview ou partenariat média :
          </p>
          <a href="mailto:press@floop.ai" className="text-xl font-semibold text-primary hover:underline">
            press@floop.ai
          </a>
        </div>
      </section>

      <PremiumFooter />
    </div>
  );
};

export default Press;
